// RHF migration
import { useQuery } from "@tanstack/react-query";
import { History, Loader2, Save } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { Search } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Input } from "@/shared/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { Form, FormControl, FormField, FormItem } from "@/shared/ui/form";
import { useMutationWithToast } from "@/shared/hooks/useMutationWithToast";
import { preferenceService, type KbDocPreference } from "../../api/preferenceService";
import { buildSceneOptions } from "./sceneOptions";
import { tagService } from "@/entities/tag";

import { PreferenceAuditSheet } from "./PreferenceAuditSheet";
import { PreferenceSlider } from "./PreferenceSlider";

const GLOBAL_SENTINEL = "__global__";

const sceneFilterSchema = z.object({
  sceneTag: z.string(),
});

type SceneFilterValues = z.infer<typeof sceneFilterSchema>;

export function ScenePreferenceCard() {
  const { t } = useTranslation("customer-service");
  const form = useForm<SceneFilterValues>({
    resolver: zodResolver(sceneFilterSchema),
    mode: "onBlur",
    defaultValues: { sceneTag: "" },
  });
  const sceneTag = form.watch("sceneTag");

  const [drafts, setDrafts] = useState<Record<string, number>>({});
  const [auditOpen, setAuditOpen] = useState(false);
  // F4:客户端 doc 名搜索(批量编辑配置面:服务端分页会切断跨行 draft + save-all,故前端过滤可见行)。
  const [docQuery, setDocQuery] = useState("");

  const tagsQ = useQuery({
    queryKey: ["kb-pref-tags"],
    queryFn: preferenceService.listSceneTags,
  });

  const tagDefsQ = useQuery({
    queryKey: ["kb-tags-defs"],
    queryFn: () => tagService.list(),
  });

  const listQ = useQuery<KbDocPreference[]>({
    queryKey: ["kb-pref-list", sceneTag],
    queryFn: () => preferenceService.list(sceneTag),
  });

  useEffect(() => {
    setDrafts({});
    setDocQuery("");
  }, [sceneTag]);

  const saveMut = useMutationWithToast({
    mutationFn: () => {
      const items = Object.entries(drafts).map(([docId, boost]) => ({ docId, boost }));
      return preferenceService.upsert(sceneTag, items);
    },
    successToast: (data) => ({
      title: t("toast.preferenceSavedTitle"),
      description: t("toast.preferenceSavedDesc", {
        updated: data.updated,
        deleted: data.deleted,
      }),
    }),
    errorToast: t("toast.preferenceSaveFailed"),
    invalidateKeys: [["kb-pref-list", sceneTag]],
    onSuccess: () => setDrafts({}),
  });

  const dirty = Object.keys(drafts).length > 0;

  const currentBoost = (pref: KbDocPreference) => drafts[pref.docId] ?? pref.boost;

  // F4:doc 名过滤(仅影响可见行;drafts 按 docId 累积、save-all 仍提交全部,搜索不清 draft)。
  const visibleDocs = useMemo(() => {
    const docs = listQ.data ?? [];
    const q = docQuery.trim().toLowerCase();
    return q ? docs.filter((p) => p.docName.toLowerCase().includes(q)) : docs;
  }, [listQ.data, docQuery]);

  const sceneOptions = useMemo(
    () =>
      [{ value: GLOBAL_SENTINEL, label: t("preference.globalOption") }].concat(
        buildSceneOptions(tagsQ.data, tagDefsQ.data)
      ),
    [tagsQ.data, tagDefsQ.data, t]
  );

  const selectValue = sceneTag === "" ? GLOBAL_SENTINEL : sceneTag;

  return (
    <Card className="shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-base">{t("preference.title")}</CardTitle>
        <div className="flex items-center gap-2">
          <Form {...form}>
            <form onSubmit={(e) => e.preventDefault()}>
              <FormField
                control={form.control}
                name="sceneTag"
                render={({ field }) => (
                  <FormItem>
                    <Select
                      value={selectValue}
                      onValueChange={(v) => field.onChange(v === GLOBAL_SENTINEL ? "" : v)}
                    >
                      <FormControl>
                        <SelectTrigger className="w-[220px]">
                          <SelectValue placeholder={t("preference.selectPlaceholder")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {sceneOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            </form>
          </Form>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAuditOpen(true)}
            title={t("preference.historyTooltip")}
          >
            <History className="h-4 w-4 mr-1" />
            {t("preference.history")}
          </Button>
          <Button size="sm" disabled={!dirty || saveMut.isPending} onClick={() => saveMut.mutate()}>
            {saveMut.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            ) : (
              <Save className="h-4 w-4 mr-1" />
            )}
            {t("preference.save")}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {listQ.isPending ? (
          <div className="text-sm text-muted-foreground fx-skeleton">{t("preference.loading")}</div>
        ) : (listQ.data ?? []).length === 0 && Object.keys(drafts).length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">
            {sceneTag === "" ? t("preference.emptyGlobal") : t("preference.emptyScene")}
          </div>
        ) : (
          <>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                aria-label={t("preference.docSearchPlaceholder")}
                placeholder={t("preference.docSearchPlaceholder")}
                className="pl-9 w-72"
                value={docQuery}
                onChange={(e) => setDocQuery(e.target.value)}
              />
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="text-left py-2 font-normal">{t("preference.headerDoc")}</th>
                  <th className="text-left py-2 font-normal w-24">
                    {t("preference.headerCurrent")}
                  </th>
                  <th className="text-left py-2 font-normal w-64">
                    {t("preference.headerAdjust")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleDocs.length === 0 && (
                  <tr>
                    <td colSpan={3} className="py-6 text-center text-sm text-muted-foreground">
                      {t("preference.emptyMatch")}
                    </td>
                  </tr>
                )}
                {visibleDocs.map((p, i) => {
                  const b = currentBoost(p);
                  const label =
                    b > 0
                      ? t("preference.boostPreferred")
                      : b < 0
                        ? t("preference.boostDemoted")
                        : t("preference.boostNeutral");
                  return (
                    <tr
                      key={p.docId}
                      className="border-b last:border-b-0 fx-enter"
                      style={{ animationDelay: `${Math.min(i, 7) * 30}ms` }}
                    >
                      <td className="py-2 truncate max-w-[360px]" title={p.docName}>
                        {p.docName}
                      </td>
                      <td className="py-2">{label}</td>
                      <td className="py-2">
                        <PreferenceSlider
                          value={b}
                          onChange={(v) =>
                            setDrafts((d) => {
                              const next = { ...d };
                              if (Math.abs(v - p.boost) < 1e-6) {
                                delete next[p.docId];
                              } else {
                                next[p.docId] = v;
                              }
                              return next;
                            })
                          }
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </CardContent>

      <PreferenceAuditSheet open={auditOpen} onOpenChange={setAuditOpen} sceneTag={sceneTag} />
    </Card>
  );
}
